/**
 * # Footer
 *
 * React component that represents the footer section of Albert.
 *
 * ## Props
 *
 * - `className` (string): Additional CSS class name for the footer.
 * - `minimal` (boolean): Flag to indicate if the footer should be rendered in minimal mode.
 */
import React from 'react';

import classnames from 'classnames';
import moment from 'moment';
import constants from 'client/constants';
import Logo from '@albert-io/atomic/atoms/Logo/Logo.react';

import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import Divider from '../../atoms/Divider/Divider.react';
import Anchor from '../../atoms/Anchor/Anchor.react';
import Text from '../../atoms/Text/Text.react';

import type {FooterColumn, FooterDataItem} from './FooterData';
import { getFooterData} from './FooterData';

import './footer.scss';

interface FooterProps extends PropsWithClassNameOptional {
  minimal?: boolean;
}

const Footer = ({className, minimal}: FooterProps) => (
  <footer className={classnames('footer', className)} role='contentinfo'>
    {!minimal && (
      <>
        <div className='footer-logo-image__wrapper mb-x6 pt-x5'>
          <Logo display='face' width='80px' />
        </div>
        <div className='footer__columns'>
          {getFooterData().map((column) => (
            <Column key={column.key} title={column.title} items={column.items} />
          ))}
        </div>
        <div className='footer-social-media'>
          <Anchor
            icon={['fab', 'facebook-f']}
            href='https://www.facebook.com/learnwithalbert/'
            rel='noopener noreferrer'
            target='_blank'
            className='footer-media mx-x2'
            standalone
          >
            Facebook
          </Anchor>
          <Anchor
            icon={['fab', 'twitter']}
            href='https://twitter.com/learnwithalbert'
            rel='noopener noreferrer'
            target='_blank'
            className='footer-media mx-x2'
            standalone
          >
            Twitter
          </Anchor>
        </div>
        <div>
          <Divider spacing={4} />
        </div>
      </>
    )}
    <div className='footer-legal'>
      <Text as='p' size='m' color='tertiary'>
        Copyright {moment().year()} Learn By Doing, Inc.
        <Anchor href='/privacy' className='ml-x2 !content-tertiary-inverse' standalone>
          Privacy Policy
        </Anchor>
        <Anchor href='/terms-of-use' className='ml-x2 !content-tertiary-inverse' standalone>
          Terms of Use
        </Anchor>
      </Text>

      <Text as='p' size='m' color='tertiary' className='footer-legal__disclaimer mt-x2 mb-x0'>
        {constants.FULL_DISCLAIMER}
      </Text>
    </div>
  </footer>
);

const Column = ({title, items}: FooterColumn) => {
  return (
    <div className='footer-column mb-x7'>
      <Text bold color='secondary' className='mb-x2' size='m' as='p'>
        {title}
      </Text>
      <ul className='footer-column__list'>
        {items.map((item: FooterDataItem) => {
          const {label, link, icon, type} = item;
          return type === 'title' ? (
            <Text bold color='secondary' className='mt-x3 mb-x2' size='m' as='p'>
              {label}
            </Text>
          ) : (
            <Anchor
              as='a'
              className='footer-item mb-x1'
              href={link}
              icon={icon as IconProp}
              standalone
            >
              {label}
            </Anchor>
          );
        })}
      </ul>
    </div>
  );
};

export default Footer;
